// Blog rules (docs/MVP.md → Blog; product decision 2026-10-10): slugs, reading time, the table
// of contents and the SEO checklist office shows while a post is written. Pure functions; the
// HTML here is the sanitized post body (apps/api/src/blog-html.ts), never raw input.

import { slugify } from "../text/slug";

export const BLOG_LIMITS = {
  titleMax: 120,
  slugMax: 80,
  /** Google shows about 60 characters of a title and about 155 of a description. */
  seoTitleIdeal: 60,
  descriptionMin: 70,
  descriptionIdeal: 155,
  descriptionMax: 300,
  summaryMax: 600,
  coverAltMax: 200,
  faqMax: 10,
  /** Below this a post is thin content. */
  wordsMin: 600,
} as const;

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Lowercase letters, digits and single hyphens, as slugify makes them. */
export function isValidSlug(slug: string): boolean {
  return slug.length > 0 && slug.length <= BLOG_LIMITS.slugMax && SLUG.test(slug);
}

/** A slug suggestion from a title: "YKS'de Netler Nasıl Artar?" → "yksde-netler-nasil-artar". */
export function suggestSlug(title: string): string {
  return slugify(title.replace(/['’]/g, "")).slice(0, BLOG_LIMITS.slugMax).replace(/-+$/, "");
}

const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
};

/** The text of an HTML fragment, entities decoded and whitespace collapsed. */
export function htmlText(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, (entity) => ENTITIES[entity] ?? entity)
    .replace(/\s+/g, " ")
    .trim();
}

export function countWords(html: string): number {
  const text = htmlText(html);
  return text ? text.split(" ").length : 0;
}

/** About 200 words a minute; at least one minute. */
export function readingMinutes(wordCount: number): number {
  return Math.max(1, Math.round(wordCount / 200));
}

export type BlogHeading = { level: 2 | 3; id: string; text: string };

/** The h2/h3 headings with their ids, for the table of contents. */
export function extractHeadings(html: string): BlogHeading[] {
  const headings: BlogHeading[] = [];
  for (const match of html.matchAll(/<h([23])\b[^>]*\bid="([^"]+)"[^>]*>([\s\S]*?)<\/h\1>/g)) {
    const text = htmlText(match[3] ?? "");
    if (text) headings.push({ level: Number(match[1]) as 2 | 3, id: match[2]!, text });
  }
  return headings;
}

/** Published and its date has come; a later date schedules the post. */
export function isLive(post: { status: string; publishedAt: Date | null }, now = new Date()) {
  return (
    post.status === "PUBLISHED" &&
    post.publishedAt !== null &&
    post.publishedAt.getTime() <= now.getTime()
  );
}

export type SeoCheckInput = {
  title: string;
  seoTitle: string | null;
  slug: string;
  description: string;
  summary: string;
  bodyHtml: string;
  coverKey: string | null;
  coverAlt: string | null;
  faqCount: number;
  categoryId: string | null;
};

export type SeoCheck = { id: string; ok: boolean; message: string };

/** The checklist office shows while a post is written; every message in Turkish. */
export function seoChecks(input: SeoCheckInput): SeoCheck[] {
  const shownTitle = (input.seoTitle || input.title).trim();
  const words = countWords(input.bodyHtml);
  const headings = extractHeadings(input.bodyHtml);
  const images = [...input.bodyHtml.matchAll(/<img\b[^>]*>/g)].map((match) => match[0]);
  const imagesWithoutAlt = images.filter((tag) => !/\balt="[^"]+"/.test(tag)).length;
  const internalLinks = [...input.bodyHtml.matchAll(/<a\b[^>]*href="(\/[^"]*)"/g)].length;
  const description = input.description.trim();
  return [
    {
      id: "title",
      ok: shownTitle.length > 0 && shownTitle.length <= BLOG_LIMITS.seoTitleIdeal,
      message: `Arama sonucundaki başlık ${shownTitle.length} karakter (en fazla ${BLOG_LIMITS.seoTitleIdeal} önerilir).`,
    },
    {
      id: "description",
      ok:
        description.length >= BLOG_LIMITS.descriptionMin &&
        description.length <= BLOG_LIMITS.descriptionIdeal,
      message: `Meta açıklama ${description.length} karakter (${BLOG_LIMITS.descriptionMin}–${BLOG_LIMITS.descriptionIdeal} önerilir).`,
    },
    {
      id: "slug",
      ok: isValidSlug(input.slug) && input.slug.length <= 60,
      message: "Adres kısa olmalı ve sadece küçük harf, rakam ve tire içermeli.",
    },
    {
      id: "summary",
      ok: input.summary.trim().length >= 80,
      message: "Özet, sorunun cevabını ilk cümlede vermeli (en az 80 karakter).",
    },
    {
      id: "words",
      ok: words >= BLOG_LIMITS.wordsMin,
      message: `Yazı ${words} kelime (en az ${BLOG_LIMITS.wordsMin} önerilir).`,
    },
    {
      id: "headings",
      ok: headings.filter((heading) => heading.level === 2).length >= 2,
      message: "En az iki ara başlık (Başlık 2) kullan; içindekiler bunlardan oluşur.",
    },
    {
      id: "cover",
      ok: Boolean(input.coverKey && input.coverAlt?.trim()),
      message: "Kapak görseli ve açıklaması (alt metin) olmalı.",
    },
    {
      id: "imageAlt",
      ok: imagesWithoutAlt === 0,
      message:
        imagesWithoutAlt === 0
          ? "Yazıdaki görsellerin hepsinin açıklaması var."
          : `${imagesWithoutAlt} görselin açıklaması (alt metin) eksik.`,
    },
    {
      id: "internalLinks",
      ok: internalLinks >= 2,
      message: `Sitedeki başka sayfalara ${internalLinks} bağlantı var (en az 2 önerilir).`,
    },
    {
      id: "faq",
      ok: input.faqCount >= 2,
      message:
        "Sık sorulan sorular bölümüne en az iki soru ekle; yapay zekâ cevaplarında kullanılır.",
    },
    {
      id: "category",
      ok: Boolean(input.categoryId),
      message: "Bir kategori seç.",
    },
  ];
}
